// RF type auto-detection — "configure one, recognise the rest".
//
// THE PREMISE, PROVED ON HARDWARE. An RF code is `transmitter(24 bits) +
// event(8 bits)`. The transmitter is unique per physical unit; the EVENT BYTE
// is the contact/button and is CONSTANT across every unit of one product. Three
// different door sensors measured on the live estate —
//   transmitter 1664514  (426115593 / 426115610)
//   transmitter 3344130  (856097289 / 856097306)
//   transmitter 15075074 (3859218953 / 3859218970)
// — all carry the SAME signature: event 26 = Open, 9 = Close. So once a company
// has labelled ONE door sensor, every later one is recognised from its event
// bytes alone.
//
// ⚠️ WHICH BYTE IS OPEN IS A HARDWARE FACT THAT WAS WRITTEN DOWN BACKWARDS.
// On this product #26 = OPEN (magnet away) and #9 = CLOSE (magnet near) —
// measured on the Safe Locker (1664514) and confirmed on "Door Sensor 1"
// (3344130), whose labels had been entered the other way round and told
// people "Open" every time the door shut (2026-10-05). This comment used to
// say the opposite, and the mistake spread from it. A label is still the
// only source of meaning — nothing derives it — so a reversed install is fixed
// with "Reverse open / close" on the sensor, never in code.
//
// ⚠️ SO ONE WRONG LABEL POISONS THE LIBRARY. Two units labelled in opposite
// ways make the byte a `conflict` and detection stops guessing it — which is
// exactly what happened, and why a conflict on the signatures page means "go
// and look at a real door", not "pick the majority".
//
// ⚠️ NO NEW STORAGE. The signature library is DERIVED from data the app already
// writes: labelling a sensor sets RfDevice.RfType and each code's
// RFREMOTE.DeviceAction. `GetRfDevices` (empty body = estate-wide, Super Admin)
// returns every sensor with its RfType and Codes[], and this file aggregates
// them into {type -> {eventByte -> meaning}}. There is no RfSignature table and
// no new endpoint.
//
// ⚠️ THE OLD WARNING STILL STANDS, NARROWED. "Which code is Open cannot be
// DERIVED, only OBSERVED" (rfTypes.js) is true for a sensor nobody has labelled.
// Here the observation has already happened — by the company, on a reference
// unit — so detection is replaying a human decision, not guessing. Where two
// units were labelled in CONFLICT (the reversed-install mistake), the byte is
// marked `conflict` and NOT auto-filled; the installer sets it and the one-click
// reverse remains.

import { getRfDevices } from '../api/api';
import { eventByteOf } from './rfCodes';

// Types that carry no reusable meaning signature: 'other' is the fallback and
// an empty type is unlabelled. Everything else (door, safe, fire, motion,
// remote, …) contributes.
const isRealType = (t) => {
  const k = String(t || '').trim().toLowerCase();
  return k && k !== 'other';
};

/**
 * Aggregate labelled sensors into a signature library.
 *
 * @param devices  GetRfDevices rows: [{ RfType, Codes: [{ REMOTEVALUE, DeviceAction }] }]
 * @returns {{ byType: Map<string, Map<number, {meaning, count, conflict}>>,
 *             byteSets: Map<string, Set<number>> }}
 *   byType[type][eventByte] = the winning meaning, how many units back it, and
 *     whether a competing meaning was ever recorded for that byte in that type.
 *   byteSets[type] = the set of event bytes known for the type (for matching).
 */
export function buildSignatures(devices) {
  // type -> eventByte -> meaning -> count
  const tally = new Map();
  (devices || []).forEach((d) => {
    const type = String(d?.RfType || '').trim().toLowerCase();
    if (!isRealType(type)) return;
    (d.Codes || []).forEach((c) => {
      const meaning = String(c?.DeviceAction || '').trim();
      if (!meaning) return;
      const code = Number(c?.REMOTEVALUE ?? c?.code);
      if (!Number.isFinite(code) || code <= 0) return;
      const byte = eventByteOf(code);
      if (!tally.has(type)) tally.set(type, new Map());
      const byByte = tally.get(type);
      if (!byByte.has(byte)) byByte.set(byte, new Map());
      const byMeaning = byByte.get(byte);
      byMeaning.set(meaning, (byMeaning.get(meaning) || 0) + 1);
    });
  });

  const byType = new Map();
  const byteSets = new Map();
  tally.forEach((byByte, type) => {
    const resolved = new Map();
    byByte.forEach((byMeaning, byte) => {
      // The winning meaning is the most-recorded; a tie or any runner-up marks
      // the byte as a conflict so Stage 2 leaves it to the installer.
      let best = null;
      let bestCount = 0;
      let total = 0;
      let tie = false;
      byMeaning.forEach((count, meaning) => {
        total += count;
        if (count > bestCount) { best = meaning; bestCount = count; tie = false; } else if (count === bestCount) { tie = true; }
      });
      resolved.set(byte, {
        meaning: best,
        count: total,
        conflict: tie || byMeaning.size > 1,
      });
    });
    byType.set(type, resolved);
    byteSets.set(type, new Set(resolved.keys()));
  });

  return { byType, byteSets };
}

/**
 * Given the event bytes a single transmitter emits, decide which type it is.
 *
 * Matches by the SET of bytes: the type whose known bytes cover the most of
 * what this transmitter sends wins. Coverage by set — not a single byte — is
 * what keeps a door's {9,26} from being read as some other type that happens to
 * reuse byte 9.
 *
 * @param eventBytes  number[] the distinct event bytes observed from one unit
 * @param sig         a buildSignatures() result
 * @returns {{ rfType, coverage, meanings: Map<number, {meaning, conflict}> }|null}
 *   null when nothing is known yet (fall back to manual).
 */
export function detectRfType(eventBytes, sig) {
  const bytes = Array.from(new Set((eventBytes || [])
    .map((b) => Number(b)).filter((b) => Number.isFinite(b))));
  if (!bytes.length || !sig || !sig.byteSets) return null;

  let best = null;
  sig.byteSets.forEach((set, type) => {
    const known = bytes.filter((b) => set.has(b));
    if (!known.length) return;
    const coverage = known.length / bytes.length;
    // Prefer higher coverage; tie-break on the more specific signature (fewer
    // stray bytes), then on more known bytes.
    const score = [coverage, -(set.size - known.length), known.length];
    if (!best || cmpScore(score, best.score) > 0) {
      best = { type, coverage, score };
    }
  });
  if (!best) return null;

  const resolved = sig.byType.get(best.type) || new Map();
  const meanings = new Map();
  bytes.forEach((b) => {
    const hit = resolved.get(b);
    // A byte unknown in the winning type, or one labelled in conflict, is left
    // without a meaning so the installer sets it (and the reverse button helps).
    if (hit && hit.meaning && !hit.conflict) meanings.set(b, { meaning: hit.meaning, conflict: false });
    else if (hit && hit.conflict) meanings.set(b, { meaning: null, conflict: true });
  });
  return { rfType: best.type, coverage: best.coverage, meanings };
}

// Lexicographic compare of the [coverage, specificity, knownCount] score arrays.
function cmpScore(a, b) {
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return 0;
}

// ── Session cache ──────────────────────────────────────────────────────────
// One estate-wide fetch per session; the learn flow and the signatures page
// share it. refresh() forces a reload after a relabel changes the library.
let cached = null;

export function loadSignatures({ force = false } = {}) {
  if (!cached || force) {
    cached = getRfDevices({})
      .then((res) => buildSignatures(Array.isArray(res?.data) ? res.data : []))
      .catch(() => buildSignatures([]));
  }
  return cached;
}

export const refreshSignatures = () => loadSignatures({ force: true });

// A flat, display-friendly view for the Super-Admin signatures page:
// [{ rfType, rows: [{ byte, meaning, count, conflict }] }], bytes ascending.
export function signatureTable(sig) {
  if (!sig || !sig.byType) return [];
  return Array.from(sig.byType.entries()).map(([rfType, byByte]) => ({
    rfType,
    rows: Array.from(byByte.entries())
      .map(([byte, v]) => ({ byte, ...v }))
      .sort((a, b) => a.byte - b.byte),
  })).sort((a, b) => a.rfType.localeCompare(b.rfType));
}
